// ✅ src/pages/VehicleV2/FuelConsumptionListing.jsx  (UPDATED FULL FILE)
// Backend mount:
//   app.use("/api/fuelConsumptionListing", fuelConsumptionListing);
//
// APIs used:
//   GET  http://nissi-india.com/api/fuelConsumptionListing/init?loginUserId=&branchId=&branchType=
//   GET  http://nissi-india.com/api/fuelConsumptionListing/list?loginUserId=&branchId=&vehicleType=&branch=&storeId=&vehicleNumber=&vehicleSubtypeId=&vehicleCode=&vehicleName=&search=&page=&limit=
//   GET  http://nissi-india.com/api/fuelConsumptionListing/viewall?branchId=&vehicleType=&branch=&storeId=&vehicleNumber=&vehicleSubtypeId=&vehicleCode=&vehicleName=
//   POST http://nissi-india.com/api/fuelConsumptionListing/delete   body: { ids:[...], loginUserId }
//
// ✅ Updated pattern like VehicleDailyProgressBulkInsert:
// - common localStorage/sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + loading + deleting states
// - safe init/list/viewall/delete handling
// - same UI card / header / button style
// - cleaner filter reset / search / pagination / selection handling

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaEye,
  FaFilter,
  FaTrash,
  FaUndo,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/fuelConsumptionListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const FuelConsumptionListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // --- UI state ---
  const [vehicleType, setVehicleType] = useState("HireVehicle");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    vehicleNumber: "",
    vehicleCode: "",
    storeId: "",
    branch: "",
    vehicleSubtypeId: "",
    vehicleName: "",
  });

  // dropdowns
  const [stores, setStores] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);

  // grid
  const [list, setList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // paging
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(toInt(total, 0) / limit)),
    [total, limit]
  );

  const isBusy = pageLoading || loading || deleting;

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white"
          : "bg-white border-gray-300 text-gray-900"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, []);

  const validateBeforeAction = useCallback(
    ({ requireBranch = false, requireLogin = false } = {}) => {
      if (requireBranch && !clean(branchId)) {
        toast.error("BranchId not found");
        return false;
      }

      if (requireLogin && !clean(loginUserId)) {
        toast.error("LoginUserId not found");
        return false;
      }

      return true;
    },
    [branchId, loginUserId]
  );

  const buildListParams = useCallback(
    (pageNo = 1) => ({
      loginUserId,
      branchId,
      vehicleType,
      search: clean(search),
      page: pageNo,
      limit,
      branch: clean(filters.branch),
      storeId: clean(filters.storeId),
      vehicleNumber: clean(filters.vehicleNumber),
      vehicleCode: clean(filters.vehicleCode),
      vehicleName: clean(filters.vehicleName),
      vehicleSubtypeId: clean(filters.vehicleSubtypeId),
    }),
    [loginUserId, branchId, vehicleType, search, limit, filters]
  );

  const buildViewAllParams = useCallback(
    () => ({
      branchId,
      vehicleType,
      branch: clean(filters.branch),
      storeId: clean(filters.storeId),
      vehicleNumber: clean(filters.vehicleNumber),
      vehicleCode: clean(filters.vehicleCode),
      vehicleName: clean(filters.vehicleName),
      vehicleSubtypeId: clean(filters.vehicleSubtypeId),
    }),
    [branchId, vehicleType, filters]
  );

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId,
          branchId,
          branchType,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        setBranches([]);
        setStores([]);
        setVehicleSubtypes([]);
        return;
      }

      const dd = data?.dropdowns || {};
      setBranches(Array.isArray(dd.branches) ? dd.branches : []);
      setStores(Array.isArray(dd.stores) ? dd.stores : []);
      setVehicleSubtypes(Array.isArray(dd.vehicleSubtypes) ? dd.vehicleSubtypes : []);

      setFilters((prev) => ({
        ...prev,
        branch: toText(branchId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Init failed");
      setBranches([]);
      setStores([]);
      setVehicleSubtypes([]);
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, branchType, commonHeaders]);

  const loadList = useCallback(
    async (pageNo = 1) => {
      try {
        const ok = validateBeforeAction({
          requireBranch: true,
          requireLogin: true,
        });
        if (!ok) return;

        setLoading(true);
        resetSelection();

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(pageNo),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setList([]);
          setTotal(0);
          toast.error(data?.message || "List load failed");
          return;
        }

        setList(Array.isArray(data.list) ? data.list : []);
        setTotal(toInt(data.total, 0));
        setPage(toInt(data.page, pageNo));
      } catch (err) {
        console.error(err);
        setList([]);
        setTotal(0);
        toast.error(err?.response?.data?.message || "List load failed");
      } finally {
        setLoading(false);
      }
    },
    [validateBeforeAction, resetSelection, buildListParams, commonHeaders]
  );

  const handleViewAll = useCallback(async () => {
    try {
      const ok = validateBeforeAction({ requireBranch: true });
      if (!ok) return;

      setLoading(true);
      resetSelection();

      const { data } = await axios.get(`${API_BASE}/viewall`, {
        params: buildViewAllParams(),
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "View all failed");
        return;
      }

      const rows = Array.isArray(data.list) ? data.list : [];
      setList(rows);
      setTotal(rows.length);
      setPage(1);

      toast.success(`Loaded ${rows.length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "View all failed");
    } finally {
      setLoading(false);
    }
  }, [validateBeforeAction, resetSelection, buildViewAllParams, commonHeaders]);

  const handleDelete = useCallback(async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one record to delete");
      return;
    }

    const ok = validateBeforeAction({ requireLogin: true });
    if (!ok) return;

    const confirmDelete = window.confirm(
      `Delete ${selectedIds.length} selected record(s)?`
    );
    if (!confirmDelete) return;

    try {
      setDeleting(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        toast.error(data?.message || "Delete failed");
        return;
      }

      toast.success(data?.message || "Deleted successfully");
      await loadList(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, loginUserId, validateBeforeAction, commonHeaders, loadList]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch("");
    setFilters({
      vehicleNumber: "",
      vehicleCode: "",
      storeId: "",
      branch: toText(branchId),
      vehicleSubtypeId: "",
      vehicleName: "",
    });
    setPage(1);
    resetSelection();
    toast.info("Filters cleared");
  }, [branchId, resetSelection]);

  const handleSearchClick = useCallback(async () => {
    setPage(1);
    await loadList(1);
  }, [loadList]);

  const handlePage = useCallback(
    async (newPage) => {
      const safePage = Math.min(Math.max(1, newPage), totalPages);
      setPage(safePage);
      await loadList(safePage);
    },
    [totalPages, loadList]
  );

  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    const ids = list.map((x) => x.id).filter((x) => x !== null && x !== undefined);
    setSelectedIds(ids);
    setSelectAll(true);
  }, [selectAll, list]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  useEffect(() => {
    const allIds = list.map((x) => x.id).filter((x) => x !== null && x !== undefined);
    const isAllSelected =
      allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
    setSelectAll(isAllSelected);
  }, [list, selectedIds]);

  const handleRowView = useCallback(
    (row) => {
      localStorage.setItem("FuelConsumptionVehicleId", String(row.id));
      localStorage.setItem(
        "FuelConsumptionType",
        vehicleType === "OwnVehicle" ? "Own" : "Hire"
      );

      navigate("/vehicle/FuelConsumption", {
        state: {
          vehicleId: row.id,
          type: vehicleType,
        },
      });
    },
    [navigate, vehicleType]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    setPage(1);
    loadList(1);
  }, [vehicleType, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">FUEL CONSUMPTION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Fuel Consumption Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : deleting
                    ? "Deleting..."
                    : loading
                    ? "Processing..."
                    : "Please wait..."}
                </span>
              )}

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${
                        showFilters ? " bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${
                        showFilters ? " bg-gray-50" : ""
                      }`
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 uppercase">
                <FaFilter />
                Filtration Area
              </h3>

              {/* Vehicle Type */}
              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      value="HireVehicle"
                      checked={vehicleType === "HireVehicle"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Hire Vehicle
                    </span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      value="OwnVehicle"
                      checked={vehicleType === "OwnVehicle"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Own Vehicle
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select Store</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    name="vehicleSubtypeId"
                    value={filters.vehicleSubtypeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select Vehicle Subtype</option>
                    {vehicleSubtypes.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>
              </div>

              {/* Search bar */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                <div className="md:col-span-2">
                  <label className={labelClass}>Search</label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={inputClass}
                    placeholder="Search by code / name / number..."
                    disabled={isBusy}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSearchClick}
                    disabled={isBusy}
                    className={`flex-1 flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Loading..." : "Search"}
                  </button>

                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-2 border rounded-lg font-semibold transition-colors ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <FaEye />
                    View All
                  </button>
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  disabled={isBusy}
                  className={`flex items-center gap-2 font-medium transition-colors duration-200 ${
                    isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-800"
                  } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <FaUndo />
                  Clear All Filters
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {isBusy && list.length === 0 ? (
              <div className="p-10 text-center">
                <div
                  className={`font-semibold ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : deleting
                    ? "Deleting..."
                    : "Loading..."}
                </div>
              </div>
            ) : list.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
                <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Supplier
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                            disabled={isBusy}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {list.map((item, idx) => (
                      <tr
                        key={`${item.id}-${idx}`}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center cursor-pointer hover:underline ${
                            isDark ? "text-blue-400" : "text-blue-600"
                          }`}
                          onClick={() => handleRowView(item)}
                          title="View"
                        >
                          {item.code}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.number}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.name}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.type}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.supplier}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            disabled={isBusy}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mt-6">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> / <b>{totalPages}</b>
              <span className="ml-3">
                Total: <b>{total}</b>
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className={`px-4 py-2 border rounded-lg ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || page <= 1 ? "opacity-70 cursor-not-allowed" : ""}`}
                disabled={isBusy || page <= 1}
                onClick={() => handlePage(page - 1)}
              >
                Prev
              </button>

              <button
                type="button"
                className={`px-4 py-2 border rounded-lg ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${
                  isBusy || page >= totalPages ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={isBusy || page >= totalPages}
                onClick={() => handlePage(page + 1)}
              >
                Next
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={isBusy || selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  isBusy || selectedIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaTrash />
                {deleting ? "Deleting..." : "Delete Selected"}
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default FuelConsumptionListing;